export type CleanupQuerySpecification = {
  clause: string;
  label: string;
  group: string;
  family?: string;
  subgroup?: string;
  order?: number;
};

export const normalizeCleanupClause = (clause: string) =>
  clause.replace(/\s+/g, " ").trim().toUpperCase();

const basicGroups: Array<[string, string[]]> = [
  ["Brownfield Properties", [
    "Assessment Grants", "Assessment Pilots", "Cleanup Grants", "RLF Grants", "RLF Pilots",
    "Multi Purpose Grants", "Area-Wide Planning Grants", "State and Tribal 128(a) Grants",
    "State and Tribal 128(a) Pilots", "Showcase Community", "Ready for Anticipated Use (Brownfield)"
  ]],
  ["Hazardous Waste Corrective Action (RCRA)", [
    "Current Corrective Action", "Remedy Selected", "Construction Complete", "Remedy Action Complete",
    "Remedy Not Yet Selected", "Ready for Anticipated Use (RCRA)"
  ]],
  ["Superfund NPL Sites", [
    "Proposed", "Final", "Deleted", "Superfund Sites Targeted for Immediate Intense Action",
    "Superfund Alternative Approach (SAA) Agreement Sites", "Ready for Anticipated Use (Superfund NPL)"
  ]],
  ["Superfund Non-NPL Sites", [
    "Assessment Needed or Ongoing", "Not Eligible for the NPL/No Further Remedial Action Planned",
    "Referred to Cleanup Program", "Ready for Anticipated Use (Superfund Non-NPL)"
  ]],
  ["Response Type", ["Emergency", "Non-Time-Critical", "Pre-Deployment", "PRP Overnight", "Time-Critical", "No Category (Response Type)"]],
  ["Response Status", ["Active", "Complete"]],
  ["Incident Category", ["Removal Action", "Removal Assessment", "No Category (Incident Category)"]],
  ["Response Authority", ["*CERCLA", "*CERCLA/*OPA", "*CWA", "*OPA", "Stafford Act", "No Category (Response Authority)"]],
  ["Federal Facilities", ["*BRAC (Federal Facilities)", "Superfund NPL", "Superfund Non-NPL", "*RCRA (Federal Facilities)"]],
  ["Recovery Act", ["Brownfields Properties", "Superfund (Recovery Act)"]],
  ["Hazardous Waste Docket", ["*NPL Superfund", "*Non-NPL Superfund", "*BRAC (Hazardous Waste Docket)", "*RCRA (Hazardous Waste Docket)", "Other Docket Facilities"]],
  ["Incidents of National Significance", ["Incidents of National Significance"]]
];

const basicExportLabels: Record<string, string> = {
  "Ready for Anticipated Use (Brownfield)": "Ready for Anticipated Use",
  "Ready for Anticipated Use (RCRA)": "Ready for Anticipated Use",
  "Ready for Anticipated Use (Superfund NPL)": "Ready for Anticipated Use",
  "Ready for Anticipated Use (Superfund Non-NPL)": "Ready for Anticipated Use",
  "No Category (Response Type)": "No Category",
  "No Category (Incident Category)": "No Category",
  "No Category (Response Authority)": "No Category"
};

export const formatBasicCleanupFilters = (labelBooleans: Record<string, boolean>) => {
  const entries = Object.entries(labelBooleans);
  const selectedCount = entries.filter(([, selected]) => selected).length;
  if (entries.length === 0 || selectedCount === 0 || selectedCount === entries.length) {
    return "no filter applied";
  }

  return basicGroups
    .map(([group, labels]) => {
      const selectedLabels = labels
        .filter((label) => labelBooleans[label])
        .map((label) => basicExportLabels[label] || label);
      return selectedLabels.length > 0 ? `${group} - [${selectedLabels.join(", ")}]` : "";
    })
    .filter(Boolean)
    .join(", ");
};

const advancedSpecifications: CleanupQuerySpecification[] = [];

const addAdvanced = (
  group: string,
  entries: Array<[string, string]>,
  family = "Basic Categories",
  subgroup?: string
) => {
  entries.forEach(([clause, label]) => advancedSpecifications.push({
    clause,
    label,
    group,
    family,
    subgroup,
    order: advancedSpecifications.length
  }));
};

addAdvanced("Brownfield Properties", [
  ["BF_ASSESS_IND = 'Y'", "Assessment Grants"],
  ["BF_ASSESS_PILOT_IND = 'Y'", "Assessment Pilots"],
  ["BF_CLEANUP_IND = 'Y'", "Cleanup Grants"],
  ["BF_RLF_GRANT_IND = 'Y'", "RLF Grants"],
  ["BF_RLF_PILOT_IND = 'Y'", "RLF Pilots"],
  ["BF_MULTIPURPOSE_IND = 'Y'", "Multi Purpose Grants"],
  ["BF_AWP_IND = 'Y'", "Area-Wide Planning Grants"],
  ["BF_128A_IND = 'Y'", "State and Tribal 128(a) Grants"],
  ["BF_128A_P_IND = 'Y'", "State and Tribal 128(a) Pilots"],
  ["BF_SHOWCASE_IND = 'Y'", "Showcase Community"],
  ["BF_READY_FOR_REUSE_IND = 'Y'", "Ready for Anticipated Use"]
]);
addAdvanced("Hazardous Waste Corrective Action (RCRA)", [
  ["RCRA_CURRENT_IND='Y'", "Current Corrective Action"],
  ["RCRA_REMEDY_SEL_IND='Y'", "Remedy Selected"],
  ["RCRA_CONSTR_COMPLT_IND='Y'", "Construction Complete"],
  ["RCRA_REMEDY_COMPLT_IND='Y'", "Remedy Action Complete"],
  ["RCRA_REMEDY_NOT_SEL_IND='Y'", "Remedy Not Yet Selected"],
  ["RCRA_RAU_IND = 'Y'", "Ready for Anticipated Use"]
]);
addAdvanced("Superfund NPL Sites", [
  ["SF_NPL_CODE_ALL = 'P' AND SF_NON_NPL_CODE = ''", "Proposed"],
  ["SF_NPL_CODE_ALL = 'F' AND SF_NON_NPL_CODE = ''", "Final"],
  ["SF_NPL_CODE_ALL = 'D' AND SF_NON_NPL_CODE = ''", "Deleted"],
  ["SF_ADMIN_FLAG='Y' AND SF_NON_NPL_CODE = ''", "Superfund Sites Targeted for Immediate Intense Action"],
  ["SAA_SITE='Y' AND SF_NON_NPL_CODE = ''", "Superfund Alternative Approach (SAA) Agreement Sites"],
  ["SF_READY_FOR_REUSE_IND = 'Y' AND SF_NPL_CODE_ALL in ('P','A','F','D') AND SF_NON_NPL_CODE = ''", "Ready for Anticipated Use"]
]);
addAdvanced("Superfund Non-NPL Sites", [
  ["SF_NON_NPL_STATUS_SUBCATEG ='Additional Assessment Needed/Ongoing'", "Additional Assessment Needed/Ongoing"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Initial Assessment Needed/Ongoing'", "Initial Assessment Needed/Ongoing"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Status not specified'", "Status not specified"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Not Eligible for the NPL/No Further Remedial Action Planned'", "Not Eligible for the NPL"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Deferred to Nuclear Regulatory Commission'", "Deferred to Nuclear Regulatory Commission"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Deferred to RCRA Subtitle C Program'", "Deferred to RCRA Subtitle C Program"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Long-term Cleanup Attention Needed/Ongoing under EPA Enforcement Authority'", "Long-term Cleanup Attention Needed/Ongoing under EPA Enforcement Authority"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Long-term Cleanup Attention Needed/Ongoing under Local Government Program'", "Long-term Cleanup Attention Needed/Ongoing under Local Government Program"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Long-term Cleanup Attention Needed/Ongoing under Other Federal Agency'", "Long-term Cleanup Attention Needed/Ongoing under Other Federal Agency"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Long-term Cleanup Attention Needed/Ongoing under State Program'", "Long-term Cleanup Attention Needed/Ongoing under State Program"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Long-term Cleanup Attention Needed/Ongoing under Tribal Program'", "Long-term Cleanup Attention Needed/Ongoing under Tribal Program"],
  ["SF_NON_NPL_STATUS_SUBCATEG ='Submitted to or Addressed by Superfund Removal Program'", "Submitted to or Addressed by Superfund Removal Program"],
  ["SF_READY_FOR_REUSE_IND = 'Y' AND SF_NON_NPL_CODE <> ''", "Ready for Anticipated Use"]
]);

addAdvanced("Brownfield Properties", [
  ["BF_IC_CODE='Y'", "Required"], ["BF_IC_CODE='N'", "Not Required"],
  ["BF_IC_CODE='U'", "No Control Information Reported"]
], "Institutional Controls", "Control requirement is");
addAdvanced("Brownfield Properties", [
  ["BF_IC_PR_IND='Y'", "Proprietary"], ["BF_IC_GC_IND='Y'", "Governmental"],
  ["BF_IC_ID_IND='Y'", "Informational Devices"], ["BF_IC_EP_IND='Y'", "Enforcement/Permit Tools"],
  ["(BF_IC_CODE='U' AND BF_IC_PR_IND='U' AND BF_IC_GC_IND='U' AND BF_IC_ID_IND='U' AND BF_IC_EP_IND='U')", "No types reported"]
], "Institutional Controls", "Control types are");
addAdvanced("Hazardous Waste Corrective Action (RCRA)", [
  ["RCRA_IC_CODE='Y'", "Controls are in place"],
  ["RCRA_IC_CODE='U'", "No controls in place or no control information reported"]
], "Institutional Controls", "Control Deployment is");
addAdvanced("Hazardous Waste Corrective Action (RCRA)", [
  ["RCRA_IC_PR_IND='Y'", "Proprietary"], ["RCRA_IC_GC_IND='Y'", "Governmental"],
  ["RCRA_IC_ID_IND='Y'", "Informational Devices"], ["RCRA_IC_EP_IND='Y'", "Enforcement/Permit Tools"],
  ["(RCRA_IC_CODE='U' and RCRA_IC_PR_IND='U' AND RCRA_IC_GC_IND='U' AND RCRA_IC_ID_IND='U' AND RCRA_IC_EP_IND='U')", "No types reported"]
], "Institutional Controls", "Controls are");
addAdvanced("Superfund NPL Sites", [
  ["SF_IC_IND='1' AND MAP_SYMBOL_CODE <> 'SN'", "Not required"],
  ["SF_IC_IND='2' AND MAP_SYMBOL_CODE <> 'SN'", "Required"],
  ["SF_IC_IND='3' AND MAP_SYMBOL_CODE <> 'SN'", "Required and Implemented"],
  ["SF_IC_IND='4' AND MAP_SYMBOL_CODE <> 'SN'", "No information is publicly available"]
], "Institutional Controls", "Controls are");
addAdvanced("Hazardous Waste Corrective Action (RCRA)", [
  ["RCRA_EC_CODE='Y'", "Groundwater Controls"], ["RCRA_EC_CODE='N'", "Non Groundwater Controls"],
  ["RCRA_EC_CODE='U'", "No Control Information Reported"]
], "Engineering Controls", "Control types are");

const addEnvironmentalIndicators = (group: string, suffix: string) => {
  addAdvanced(group, [
    [`SF_EI_HE_CODE='Y' AND ${suffix}`, "Under Control"],
    [`SF_EI_HE_CODE='N' AND ${suffix}`, "Not Under Control"],
    [`SF_EI_HE_CODE='U' AND ${suffix}`, "No Control Information Reported"]
  ], "Environmental Indicators", "Human Exposures are");
  addAdvanced(group, [
    [`SF_EI_GM_CODE='Y' AND ${suffix}`, "Under Control"],
    [`SF_EI_GM_CODE='N' AND ${suffix}`, "Not Under Control"],
    [`SF_EI_GM_CODE='U' AND ${suffix}`, "No Control Information Reported"]
  ], "Environmental Indicators", "Groundwater Migration is");
};

addAdvanced("Hazardous Waste Corrective Action (RCRA)", [
  ["RCRA_EI_HE_CODE='Y'", "Under Control"], ["RCRA_EI_HE_CODE='N'", "Not Under Control"],
  ["RCRA_EI_HE_CODE='U'", "No Control Information Reported"]
], "Environmental Indicators", "Human Exposures are");
addAdvanced("Hazardous Waste Corrective Action (RCRA)", [
  ["RCRA_EI_GM_CODE='Y'", "Under Control"], ["RCRA_EI_GM_CODE='N'", "Not Under Control"],
  ["RCRA_EI_GM_CODE='U'", "No Control Information Reported"]
], "Environmental Indicators", "Groundwater Migration is");
addEnvironmentalIndicators("Superfund NPL Sites", "MAP_SYMBOL_CODE <> 'SN'");
addEnvironmentalIndicators("Superfund Non-NPL Sites", "MAP_SYMBOL_CODE LIKE '%SN%'");

const advancedByClause = new Map(
  advancedSpecifications.map((specification) => [normalizeCleanupClause(specification.clause), specification])
);

export const formatAdvancedCleanupFilters = (clauses: string[], matchesAny: boolean) => {
  if (clauses.length === 0) return "no advanced filter applied";

  const selected: CleanupQuerySpecification[] = clauses.map((clause) => advancedByClause.get(normalizeCleanupClause(clause)) || {
    clause,
    label: clause,
    group: "Other",
    family: "Other",
    order: Number.MAX_SAFE_INTEGER
  }).sort((first, second) => (first.order || 0) - (second.order || 0));
  const groups = Array.from(new Set(selected.map(({ group }) => group)));
  const groupText = groups.map((group) => {
    const groupSelections = selected.filter((specification) => specification.group === group);
    const families = Array.from(new Set(groupSelections.map(({ family }) => family || "Selections")));
    const familyText = families.map((family) => {
      const familySelections = groupSelections.filter((specification) => (specification.family || "Selections") === family);
      const subgroups = Array.from(new Set(familySelections.map(({ subgroup }) => subgroup).filter(Boolean))) as string[];
      const directLabels = familySelections.filter(({ subgroup }) => !subgroup).map(({ label }) => label);
      const parts = directLabels.length > 0 ? [directLabels.join(", ")] : [];
      subgroups.forEach((subgroup) => {
        const labels = familySelections.filter((specification) => specification.subgroup === subgroup).map(({ label }) => label);
        parts.push(`${subgroup}: [${labels.join(", ")}]`);
      });
      return family === "Basic Categories"
        ? parts.join(", ")
        : `${family} - [${parts.join(", ")}]`;
    });
    return `${group} - [${familyText.join(", ")}]`;
  });

  return `${matchesAny ? "ANY" : "ALL"}: ${groupText.join(", ")}`;
};
